import React, { useContext, useRef, useState } from "react"
import clsx from "clsx"
import debounce from "lodash.debounce"
import { GatsbyImage } from "gatsby-plugin-image"
import { getShopifyImage } from "gatsby-source-shopify"

import { StoreContext } from "@context/StoreContext"
import QuickBuyContext from "@context/QuickBuyContext"

//import HeartIcon from '@components/common/HeartIcon'
import SoftButton from "@components/common/SoftButton"
import Tag from "@components/common/Tag"
import {
	DotIcon,
	MinusCircleIcon,
	PlusCircleIcon,
	TrashIcon,
} from "@phosphor-icons/react"
//import SaveToWishList from '@components/common/SaveToWishList'
import { formatPrice } from "@utils"
import { GA_EVENTS } from "@utils/analytics/constants"
import { sendGAEvent } from "@utils/analytics"

const LineItem = ({ item, className }) => {
	const { loading, removeLineItem, updateLineItem } = useContext(StoreContext)
	const { setShowBag } = useContext(QuickBuyContext)

	const [quantity, setQuantity] = useState(item.quantity)
	const [swiped, setSwiped] = useState(false)
	const [itemSetForRemove, setItemSetForRemove] = useState(false)
	const startX = useRef(0)

	const handlePointerDown = (e) => {
		if(item?.cost?.subtotalAmount?.amount <= 0) return
		startX.current = e.clientX || e.touches?.[0]?.clientX
	}

	const handlePointerUp = (e) => {
		if(item?.cost?.subtotalAmount?.amount <= 0) return
		const endX = e.clientX || e.changedTouches?.[0]?.clientX
		if (startX.current - endX > 80) {
			// swiped left
			setSwiped(true)
		}
	}

	const handleNavigate = () => {
		setShowBag(false)
		window.location = `/products/${item?.merchandise?.product?.handle}`
	}

	const subtotal = formatPrice(
		item.cost.totalAmount.currencyCode,
		item.cost.totalAmount.amount
	)

	const variantImage = React.useMemo(
		() => ({
			...item.merchandise.image,
			originalSrc: item.merchandise.image.originalSrc,
		}),
		[item.merchandise.image]
	)

	const image = React.useMemo(
		() =>
			getShopifyImage({
				image: variantImage,
				layout: "constrained",
				crop: "contain",
				width: 262,
				height: 390,
			}),
		[variantImage]
	)

	const title = item?.merchandise?.product?.title?.split(" / ")?.[0]
	const variantName = item?.merchandise?.product?.title
		?.split(" / ")?.[1]
		?.replace("FINAL SALE", "")
	const isFinalSale = item?.merchandise?.product?.title?.includes("FINAL SALE")

	const handleRemove = async () => {
		setItemSetForRemove(true)
		const cartData = await removeLineItem(item?.id)
		if (!cartData) {
			setItemSetForRemove(false)
			return
		}
		// console.log("item to remove:", item)
		const { cost, merchandise } = item
		sendGAEvent(GA_EVENTS.REMOVE_FROM_CART, {
			currency: cost?.subtotalAmount?.currencyCode,
			value: cost?.subtotalAmount?.amount,
			items: [
				{
					item_id: merchandise?.id,
					item_name: merchandise?.product?.title,
					affiliation: "Blackbough Swim",
					// coupon: "SUMMER_FUN",
					discount:
						parseFloat(cost?.compareAtAmountPerQuantity?.amount) -
						parseFloat(cost?.subtotalAmount?.amount),
					// index: 0,
					item_brand: "Blackbough Swim",
					item_category: "Apparel",
					item_category2: merchandise?.title,
					// item_category3: "Shirts",
					// item_category4: "Crew",
					// item_category5: "Short sleeve",
					// item_list_id: "related_products",
					// item_list_name: "Related Products",
					item_variant: merchandise?.id,
					// location_id: "ChIJIQBpAG2ahYAR_6128GcTUEo",
					price: parseFloat(cost?.subtotalAmount?.amount),
					quantity: 1,
				},
			],
		})
		if (cartData) setItemSetForRemove(false)
	}

	const uli = debounce((value) => updateLineItem(item.id, value), 300)
	// eslint-disable-next-line
	const debouncedUli = React.useCallback((value) => uli(value), [])

	const handleQuantityChange = (value) => {
		if (value !== "" && Number(value) < 1) {
			return
		}

		setQuantity(value)

		if (Number(value) >= 1) {
			debouncedUli(value)
		}
	}

	const doIncrement = () => {
		handleQuantityChange(Number(quantity || 0) + 1)
	}

	const doDecrement = () => {
		handleQuantityChange(Number(quantity || 0) - 1)
	}

	return (
		<div
			className={clsx("relative", className, itemSetForRemove && 'pointer-events-none opacity-50')}
			onTouchStart={handlePointerDown}
			onTouchEnd={handlePointerUp}
		>
			{/* <div
				className={clsx(
					"absolute left-0 top-0 bg-black/75 w-full h-full flex flex-col items-center justify-center gap-4 z-20 transition-all duration-300 ease-in-out",
					swiped
						? "opacity-100 scale-100 translate-x-0"
						: "opacity-0 scale-95 translate-x-full pointer-events-none"
				)}
			>
				<p className="text-white text-md font-medium">Remove Item?</p>
				<div className="flex gap-x-6">
					<button
						className="btn-outline !bg-white/75 rounded-full w-24"
						onTouchEnd={() => setSwiped(false)}
					>
						No
					</button>
					<button
						className="btn-primary !bg-danger rounded-full w-24"
						onTouchEnd={() => {
							setSwiped(false) 
							handleRemove()
						}}
					>
						Yes
					</button>
				</div>
			</div> */}
			<div className="w-full flex flex-nowrap gap-x-4">
				<button
					className="w-20 h-30 shrink-0 cursor-pointer"
					onClick={() => handleNavigate()}
				>
					{image && (
						<GatsbyImage
							key={variantImage.src}
							image={image}
							alt={variantImage.altText ?? item.merchandise.product.title}
						/>
					)}
				</button>

				<div className="pt-1 flex flex-col w-full">
					<a
						href={`/products/${item?.merchandise?.product?.handle}`}
						className="flex flex-nowrap gap-x-2 cursor-pointer text-left"
						onClick={() => setShowBag(false)}
					>
						<p className="flex-1 font-semibold">{title}</p>
					</a>

					<div className="flex flex-nowrap items-center text-sm">
						<p className="text-tertiary">{variantName}</p>
						<DotIcon size={20} weight="bold" />
						<p className="text-tertiary">
							{item.merchandise.title === "+"
								? "Free Size"
								: item.merchandise.title}
						</p>
					</div>

					{isFinalSale && (
						<div className="flex gap-2 text-xs font-semibold pt-2">
							<Tag label={"Final Sale"} className="border border-gray-300" />
						</div>
					)}

					<div className="flex flex-nowrap justify-between items-end py-4">
						<div className="grid grid-cols-3 gap-x-2 items-center">
							{!title.includes("FREE") ? (
								<>
									<MinusCircleIcon
										size={24}
										weight={"light"}
										className={`${
											quantity > 1 ? "cursor-pointer" : "text-gray-300"
										}`}
										onClick={() => doDecrement()}
									/>
									<span className="text-center">{quantity}</span>
									<PlusCircleIcon
										size={24}
										weight={"light"}
										className="cursor-pointer"
										onClick={() => doIncrement()}
									/>
								</>
							) : null}
						</div>

						<p className="text-tertiary">
							{!title.includes("FREE") ? subtotal : "FREE"}
						</p>
					</div>

					{/*
                            showWarning && (
                            <div className="p-2 text-red-600 flex flex-nowrap gap-x-2 text-xs items-start border border-red-600">
                                <WarningCircle size={24} weight="light" />
                                <p>
                                    Sorry, but you can’t add more of this product to the cart.
                                </p>
                            </div>
                            )
                        */}
				</div>
			</div>

			<div className="flex gap-x-3 flex-1 text-[13px] items-center justify-end py-4 border-b border-gray-200">
				{/* <SaveToWishList onClick={() => handleSave()} />
                <div className="text-secondary">|</div> */}

				{!title.includes("FREE") ? (
					<SoftButton
						label={"REMOVE"}
						disabled={loading}
						onClick={() => handleRemove()}
						iconPosition="start"
						iconGap="gap-x-2"
						icon={<TrashIcon size={16} weight="light" />}
						ariaLabel={"Remove Item"}
					/>
				) : null}
			</div>
		</div>
	)
}

export default LineItem
